import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  OnDestroy,
  OnInit
} from "@angular/core";
import { NonNullableFormBuilder, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MusicSourceService } from "@onsip/common/services/api/resources/musicSource/music-source.service";
import { MohAddressService } from "@onsip/common/services/api/resources/mohAddress/moh-address.service";
import { BehaviorSubject, combineLatest, from, Observable, of, Subscription } from "rxjs";
import { debounceTime, filter, map, pluck, share, switchMap, take, tap } from "rxjs/operators";
import { MusicSource } from "@onsip/common/services/api/resources/musicSource/music-source";
import {
  ApiPromiseState,
  ApiPromiseSubject
} from "@onsip/common/services/api/api-promise-state.service";
import { MohAddress } from "@onsip/common/services/api/resources/mohAddress/moh-address";
import { TranslateService } from "@ngx-translate/core";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";
import { DynamicPricingService } from "@onsip/common/services/dynamic-pricing.service";
import { UserPackagePlan } from "@onsip/common/services/api/resources/package/package";
import { views } from "@onsip/web/app/phone/views";
import { ModalMaterialComponent } from "@onsip/web/features/shared/components/modal/modal-material.component";
import { EditMusicSourceDialogComponent } from "@onsip/web/features/shared/components/editMusicSourceDialog/edit-music-source-dialog.component";
import { BreakpointObserver } from "@angular/cdk/layout";
import { AdminTableAbstractComponent } from "@onsip/web/features/abstractClass/admin-table-abstract.component";
import { E2eLocators } from "@onsip/e2e/configs/locators";
import { DecimalPipe } from "@angular/common";

const DEFAULT_MUSIC_SOURCE_URL = "local_stream://classical";

@Component({
  selector: "onsip-music-on-hold-page",
  templateUrl: "./music-on-hold-page.component.html",
  styleUrls: ["./music-on-hold-page.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DecimalPipe],
  standalone: false
})
export class MusicOnHoldPageComponent
  extends AdminTableAbstractComponent<MusicSource>
  implements OnInit, OnDestroy
{
  @HostBinding("class.onsip-grid-content")
  @HostBinding("class.onsip-admin-host-grid")
  _dontUse = true;

  protected unsubscriber = new Subscription();
  protected displayedColumnsRecord = {
    XSmall: ["name", "actions"],
    Small: ["name", "sources", "actions"],
    Medium: ["name", "sources", "actions"],
    Large: ["name", "sources", "actions"],
    XLarge: ["name", "sources", "actions"]
  };

  E2eLocators = E2eLocators;
  isEditMode$ = new BehaviorSubject(false);
  loadingStateComplete$ = new BehaviorSubject(false);
  text = "ONSIP_I18N.PLEASE_WAIT_SECONDS";
  musicOnHoldForm = this.fb.group({
    isEnabled: [false],
    channelsAmount: [0, [Validators.required, Validators.min(0), Validators.max(200)]],
    defaultSource: [<MusicSource | undefined>undefined, [Validators.required]],
    isEnhanced: [false]
  });
  channelOptions = Array.from(Array(201), (_, index) => index);
  views = views;
  maxChannels!: Array<number>;
  currentSource$ = new BehaviorSubject<MusicSource | undefined>(undefined);
  currentMohAddress$ = new BehaviorSubject({} as MohAddress);
  mohPricingProperties$ = this.dynamicPricingService.getPackageInfo$("AdvMohChannel").pipe(share());
  mohEnhancedPricingProperties$ = this.dynamicPricingService
    .getPackageInfo$("AdvMohEnhanced")
    .pipe(share());
  pricingTexts: Record<UserPackagePlan, string> = {
    [UserPackagePlan.UNLIMITED]: "ONSIP_I18N.MOH_PRICING_UNLIMTED",
    [UserPackagePlan.FREE]: "ONSIP_I18N.MOH_PRICING_FREE",
    [UserPackagePlan.BASIC]: "ONSIP_I18N.MOH_PRICING_BASIC"
  };
  pricingExampleTexts: Record<UserPackagePlan, string> = {
    [UserPackagePlan.UNLIMITED]: "ONSIP_I18N.MOH_PRICING_LINES_EXAMPLE_UNLIMITED",
    [UserPackagePlan.FREE]: "ONSIP_I18N.MOH_PRICING_LINES_EXAMPLE",
    [UserPackagePlan.BASIC]: "ONSIP_I18N.MOH_PRICING_LINES_EXAMPLE"
  };
  userPackagePlan = UserPackagePlan;

  private dialogOptions = {
    panelClass: ["mat-typography", "onsip-dialog-universal-style"],
    restoreFocus: false,
    autoFocus: false
  };

  constructor(
    protected matDialog: MatDialog,
    private musicSourceService: MusicSourceService,
    private mohAddressService: MohAddressService,
    private translateService: TranslateService,
    private snackbarService: SnackbarService,
    private dynamicPricingService: DynamicPricingService,
    private decimalPipe: DecimalPipe,
    protected translate: TranslateService,
    protected breakpointObserver: BreakpointObserver,
    protected cdr: ChangeDetectorRef,
    private fb: NonNullableFormBuilder
  ) {
    super(breakpointObserver, cdr, matDialog, translate);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.initEnhanceEditingStream();
    this.initDisableStream();
    this.onChannelsAmountChange();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.unsubscriber.unsubscribe();
  }

  protected getItemValueByColumn(item: MusicSource, property: string): string | number {
    switch (property) {
      case "name":
        return item.name.toLowerCase();
      case "sources":
        return item.url?.toLowerCase();
      default:
        return item.name.toLowerCase();
    }
  }

  protected initDataSource(): void {
    this.mohAddressService.mohAddressBrowse();
    this.musicSourceService.musicSourceBrowse();
    this.unsubscriber.add(
      combineLatest({
        mohAddressResponse: this.mohAddressService.orgState.pipe(map(state => state[0])),
        musicSourcesResponse: this.musicSourceService.orgState.pipe(
          filter(musicSources => !!musicSources.length),
          map(musicSources => musicSources)
        )
      }).subscribe(({ mohAddressResponse, musicSourcesResponse }) => {
        this.dataSource.data = this.groupDataSources(musicSourcesResponse);
        const defaultSource = musicSourcesResponse.find(source => source.default);
        this.currentMohAddress$.next(mohAddressResponse);
        this.currentSource$.next(defaultSource);
        this.isDataObtained$.next(true);
        if (this.musicOnHoldForm.untouched) {
          this.musicOnHoldForm.patchValue(
            {
              isEnabled: !!mohAddressResponse.maxChannels,
              channelsAmount: mohAddressResponse.maxChannels,
              defaultSource,
              isEnhanced: mohAddressResponse.enhanced
            },
            { emitEvent: false }
          );
          this.musicOnHoldForm.markAsTouched();
        } else {
          // this patch is needed to have link to the same object that is in dataSource
          // because mat-radio-group doesn't have compareWith function
          this.musicOnHoldForm.patchValue(
            {
              defaultSource: this.dataSource.data.find(
                ({ musicSourceId }) =>
                  musicSourceId === this.musicOnHoldForm.value.defaultSource?.musicSourceId
              )
            },
            { emitEvent: false }
          );
        }
        !this.maxChannels && this.loadingStateComplete$.next(true);
        this.maxChannels = [mohAddressResponse.maxChannels];
        this.initTrackEnabled();
        this.text = "ONSIP_I18N.SAVING_CHANGES";
      })
    );
  }

  protected getItemName({ name }: MusicSource): string {
    return name;
  }

  compareWithSources(source1: MusicSource, source2: MusicSource): boolean {
    return source1.musicSourceId === source2.musicSourceId;
  }

  manageMusicOnHold(): void {
    this.matDialog.open(EditMusicSourceDialogComponent, this.dialogOptions);
  }

  onEdit(): void {
    this.isEditMode$.next(true);
    this.musicOnHoldForm.controls["channelsAmount"].patchValue(
      this.maxChannels.length && this.maxChannels[0] ? this.maxChannels[0] : 1,
      { emitEvent: false }
    );
  }

  onEditSource(source: MusicSource): void {
    this.matDialog.open(EditMusicSourceDialogComponent, {
      ...this.dialogOptions,
      data: source
    });
  }

  trackById(index: number, item: MusicSource): string {
    return item.musicSourceId;
  }

  protected performDeleteRequest(source: MusicSource): void {
    const selectedSourceId = this.musicOnHoldForm.controls["defaultSource"].value?.musicSourceId;
    this.musicSourceService.musicSourceDelete(source.musicSourceId).then(response => {
      if (response.status === "success") {
        this.unsubscriber.add(
          this.translateService
            .get("ONSIP_I18N.MUSIC_SOURCE_WAS_DELETED")
            .pipe(take(1))
            .subscribe(message => {
              this.snackbarService.openSnackBar(message, "success");

              if (selectedSourceId === source.musicSourceId) {
                this.musicOnHoldForm.controls["defaultSource"].patchValue(this.getDefaultSource());
              }
            })
        );
      }
    });
  }

  private onChannelsAmountChange(): void {
    this.unsubscriber.add(
      this.musicOnHoldForm.controls["channelsAmount"].valueChanges
        .pipe(
          switchMap(value => {
            if (this.maxChannels.length < 2) {
              this.maxChannels = [...this.maxChannels, value];
            } else {
              this.maxChannels[0] = this.maxChannels[1];
              this.maxChannels[1] = value;
            }

            return this.getLinesConfirmResult$(this.maxChannels[1]).pipe(
              map(isConfirmed => (isConfirmed ? this.maxChannels[1] : this.maxChannels[0]))
            );
          })
        )
        .subscribe(value => {
          this.musicOnHoldForm.controls["channelsAmount"].setValue(value, {
            emitEvent: false
          });
        })
    );
  }

  private initDisableStream(): void {
    this.unsubscriber.add(
      this.musicOnHoldForm.controls.isEnabled.valueChanges
        .pipe(
          filter(
            isEnabled => !isEnabled && !!this.currentMohAddress$.value.maxChannels !== isEnabled
          ),
          tap(() => {
            this.musicOnHoldForm.patchValue(
              {
                channelsAmount: 0,
                isEnhanced: false
              },
              { emitEvent: false }
            );
          }),
          switchMap(() =>
            this.editMohAddress({
              ...this.currentMohAddress$.value,
              maxChannels: 0,
              enhanced: false
            })
          )
        )
        .subscribe(mohAddressResponse => {
          const musicSource = this.getDefaultSource();
          if (musicSource) {
            this.musicSourceService.musicSourceOrganizationDefault(musicSource.musicSourceId);
          }

          this.handleMohAddressEditResponse(mohAddressResponse);
        })
    );
  }

  onSubmit(): void {
    if (this.musicOnHoldForm.valid) {
      this.loadingStateComplete$.next(false);
      const maxChannels = this.musicOnHoldForm.controls["channelsAmount"].value;
      const musicSourceId =
        !this.isDisableStreamType() && !maxChannels
          ? this.getDefaultSource()?.musicSourceId
          : this.musicOnHoldForm.controls["defaultSource"].value?.musicSourceId;
      if (!maxChannels) {
        this.musicOnHoldForm.patchValue(
          {
            isEnabled: false,
            isEnhanced: false
          },
          { emitEvent: false }
        );
      }
      this.unsubscriber.add(
        from(
          Promise.all([
            this.editMohAddress({
              ...this.currentMohAddress$.value,
              ...(!maxChannels
                ? { enhanced: false }
                : { enhanced: this.musicOnHoldForm.get("isEnhanced")?.value || false }),
              maxChannels
            }),
            this.musicSourceService.musicSourceOrganizationDefault(musicSourceId || "1")
          ])
        ).subscribe(([mohAddressResponse]) => this.handleMohAddressEditResponse(mohAddressResponse))
      );
    }
  }

  private initEnhanceEditingStream(): void {
    this.unsubscriber.add(
      this.musicOnHoldForm.controls["isEnhanced"].valueChanges
        .pipe(
          switchMap(isEnhanced => {
            if (isEnhanced) {
              return this.getEnhanceConfirm$();
            } else {
              return this.getDefaultConfirm$();
            }
          })
        )
        .subscribe(isConfirmed => {
          if (!isConfirmed) {
            this.musicOnHoldForm.patchValue(
              {
                isEnhanced: !this.musicOnHoldForm.controls.isEnhanced.value
              },
              { emitEvent: false }
            );
          } else {
            if (!this.musicOnHoldForm.controls.isEnhanced.value) {
              this.musicOnHoldForm.patchValue(
                {
                  defaultSource: this.isDisableStreamType()
                    ? this.getDefaultSource()
                    : this.musicOnHoldForm.controls["defaultSource"].value
                },
                { emitEvent: false }
              );
            }
          }
        })
    );
  }

  private getDefaultSource(): MusicSource | undefined {
    return this.dataSource.data.find(source => source.url === DEFAULT_MUSIC_SOURCE_URL);
  }

  private isDisableStreamType(): boolean {
    return (
      !this.musicOnHoldForm.controls["isEnhanced"].value &&
      !this.musicOnHoldForm.controls["defaultSource"].value?.url?.startsWith("local_stream")
    );
  }

  private updateLocalMohAddress(mohAddressResponse: ApiPromiseSubject<MohAddress>): void {
    if (mohAddressResponse.status === "success") {
      this.currentMohAddress$.next(
        mohAddressResponse.data[Object.keys(mohAddressResponse.data)[0]]
      );
    }
  }

  private editMohAddress(mohAddress: MohAddress): ApiPromiseState<MohAddress> {
    return this.mohAddressService.mohAddressEdit({
      Address: this.currentMohAddress$.value.username + "@" + this.currentMohAddress$.value.domain,
      Enhanced: mohAddress.enhanced.toString(),
      MaxChannels: mohAddress.maxChannels.toString()
    });
  }

  private handleMohAddressEditResponse(
    mohAddressResponse: ApiPromiseSubject<MohAddress>,
    turnOffEditMode = true
  ): void {
    this.loadingStateComplete$.next(true);
    if (mohAddressResponse.status === "success") {
      this.updateLocalMohAddress(mohAddressResponse);
      if (turnOffEditMode) {
        this.isEditMode$.next(false);
      }

      if (!mohAddressResponse.data[this.currentMohAddress$.value.addressId].maxChannels) {
        this.openSnackBar("ONSIP_I18N.MUSIC_ON_HOLD_WAS_DISABLED");
      } else {
        this.openSnackBar("ONSIP_I18N.YOUR_CHANGES_HAVE_BEEN_SAVED");
      }
    } else {
      this.musicOnHoldForm.patchValue(
        {
          isEnabled: !!this.currentMohAddress$.value.maxChannels,
          channelsAmount: this.currentMohAddress$.value.maxChannels,
          isEnhanced: this.currentMohAddress$.value.enhanced
        },
        { emitEvent: false }
      );
      this.openSnackBar("ONSIP_I18N.WE_JUST_ENCOUNTERED_SOMETHING_ODD");
    }
  }

  private getEnhanceConfirm$(): Observable<boolean> {
    return this.mohEnhancedPricingProperties$.pipe(
      switchMap(pricing => {
        if (!pricing) {
          return of(false);
        }

        if (pricing.packagePlan === UserPackagePlan.UNLIMITED) {
          return of(true);
        }

        const dialog = this.matDialog.open(ModalMaterialComponent, {
          ...this.dialogOptions,
          data: {
            title: this.translateService.instant(
              "ONSIP_I18N.UPGRADE_TO_ENHANCED_MUSIC_ON_HOLD_CONFIRM_TITLE"
            ),
            message: this.translateService.instant(
              "ONSIP_I18N.UPGRADE_TO_ENHANCED_MUSIC_ON_HOLD_CONFIRM_MESSAGE",
              { price: pricing.featurePricing }
            ),
            primaryBtnText: this.translateService.instant("ONSIP_I18N.CONFIRM"),
            secondaryBtnText: this.translateService.instant("ONSIP_I18N.CANCEL"),
            primaryBtnFlat: true
          }
        });

        return dialog.afterClosed().pipe(take(1), pluck("doPrimaryAction"));
      })
    );
  }

  private getDefaultConfirm$(): any {
    const dialog = this.matDialog.open(ModalMaterialComponent, {
      ...this.dialogOptions,
      data: {
        title: this.translateService.instant("ONSIP_I18N.DOWNGRADE_TO_DEFAULT_MUSIC_ON_HOLD"),
        message: this.translateService.instant(
          "ONSIP_I18N.YOU_WILL_NO_LONGER_HAVE_ACCESS_TO_ENHANCED_MUSIC_STREAMS"
        ),
        primaryBtnText: this.translateService.instant("ONSIP_I18N.CONFIRM"),
        secondaryBtnText: this.translateService.instant("ONSIP_I18N.CANCEL"),
        primaryBtnFlat: true
      }
    });
    return dialog.afterClosed().pipe(take(1), pluck("doPrimaryAction"));
  }

  private getLinesConfirmResult$(maxChannels: number): Observable<boolean> {
    if (!maxChannels) {
      return of(true);
    }

    return this.mohPricingProperties$.pipe(
      switchMap(pricing => {
        if (!pricing) {
          return of(false);
        }
        if (
          pricing.packagePlan === UserPackagePlan.UNLIMITED ||
          pricing.featureLimit >= maxChannels
        ) {
          return of(true);
        }

        const channelsToCountPrice = maxChannels - pricing.featureLimit;
        const dialog = this.matDialog.open(ModalMaterialComponent, {
          ...this.dialogOptions,
          data: {
            title: this.translateService.instant("ONSIP_I18N.ADD_MORE_CHANNELS", {
              amount: maxChannels
            }),
            message: this.translateService.instant(
              "ONSIP_I18N.ENABLING_MUSIC_ON_HOLD_CHARGE_CONIRM_MESSAGE",
              {
                amount: maxChannels,
                price: this.decimalPipe.transform(
                  (parseFloat(pricing.featurePricing) * 100 * channelsToCountPrice) / 100,
                  "1.2-2"
                )
              }
            ),
            primaryBtnText: this.translateService.instant("ONSIP_I18N.CONFIRM"),
            secondaryBtnText: this.translateService.instant("ONSIP_I18N.CANCEL"),
            primaryBtnFlat: true
          }
        });

        return dialog.afterClosed().pipe(take(1), pluck("doPrimaryAction"));
      })
    );
  }

  onCancelEdit(): void {
    const dialogRef = this.matDialog.open(ModalMaterialComponent, {
      panelClass: ["mat-typography", "onsip-dialog-universal-style"],
      data: {
        title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
        message: this.translate.instant("ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"),
        primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
        secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
        primaryBtnFlat: true
      }
    });

    this.unsubscriber.add(
      dialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe(changeSuccess => {
          if (changeSuccess?.doPrimaryAction) {
            this.isEditMode$.next(false);
            this.musicOnHoldForm.patchValue(
              {
                isEnabled: !!this.currentMohAddress$.value.maxChannels,
                channelsAmount: this.currentMohAddress$.value.maxChannels,
                defaultSource: this.currentSource$.value,
                isEnhanced: this.currentMohAddress$.value.enhanced
              },
              { emitEvent: false }
            );
          }
        })
    );
  }
  private groupDataSources(elements: Array<MusicSource>): Array<MusicSource> {
    const defaultStreams = elements.filter(element => element.url.startsWith("local_stream"));
    const customStreams = elements.filter(element => !element.url.startsWith("local_stream"));
    return [...customStreams, ...defaultStreams];
  }

  private initTrackEnabled(): void {
    this.unsubscriber.add(
      this.musicOnHoldForm.controls.isEnabled.valueChanges
        .pipe(debounceTime(100))
        .subscribe(isEnabled => {
          if (isEnabled) {
            this.onEdit();
          }
        })
    );
  }

  private openSnackBar(text: string) {
    this.unsubscriber.add(
      this.translateService
        .get(text)
        .pipe(take(1))
        .subscribe(message => this.snackbarService.openSnackBar(message, "success"))
    );
  }
}
